import { useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../shared/ui/Modal';
import { Button } from '../../shared/ui/Button';
import { Input } from '../../shared/ui/Input';
import { Select } from '../../shared/ui/Select';
import { Alert } from '../../shared/ui/Alert';
import { FormField } from '../../shared/ui/FormField';
import { db, storage } from '../../shared/lib/firebase';
import { useAuth } from '../auth/useAuth';
import { useToast } from '../../shared/hooks/useToast';
import { useSupportSettings } from './hooks/useSupportSettings';
import { PRIORITY_LABELS } from './lib/support';
import type { SupportAttachment, SupportPriority } from './lib/types';
import styles from './OpenTicketModal.module.css';

export function OpenTicketModal({ open, onClose, originPath }: {
  open: boolean;
  onClose: () => void;
  originPath: string | null;
}) {
  const { currentUser, userDoc } = useAuth();
  const { settings } = useSupportSettings();
  const toast = useToast();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState<SupportPriority>('normal');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);

  const categoryOptions = [
    { value: '', label: '— selecione —' },
    ...settings.categories.map((c) => ({ value: c, label: c })),
  ];
  const priorityOptions = settings.priorities.map((p) => ({
    value: p.key,
    label: PRIORITY_LABELS[p.key],
  }));

  function reset() {
    setSubject('');
    setCategory('');
    setPriority('normal');
    setBody('');
    setFiles([]);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleClose() {
    if (saving) return;
    onClose();
  }

  async function submit() {
    if (!currentUser || !subject.trim() || !body.trim()) {
      toast.warning('Preencha o assunto e a descrição.');
      return;
    }
    setSaving(true);
    try {
      const ticketRef = doc(collection(db, 'support_tickets'));
      const messageRef = doc(collection(db, 'support_tickets', ticketRef.id, 'messages'));
      const attachments: SupportAttachment[] = [];
      for (const f of files) {
        const path = `support_attachments/${ticketRef.id}/${messageRef.id}/${Date.now()}_${f.name}`;
        await uploadBytes(ref(storage, path), f, { contentType: f.type });
        attachments.push({ path, name: f.name, contentType: f.type, size: f.size });
      }
      const name = userDoc?.name ?? currentUser.displayName ?? currentUser.email ?? 'Usuário';
      const batch = writeBatch(db);
      batch.set(ticketRef, {
        subject: subject.trim(),
        category: category || null,
        priority,
        status: 'open',
        createdBy: currentUser.uid,
        createdByName: name,
        originPath,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
        lastMessageBy: 'owner',
        messageCount: 0,
        unreadForOwner: false,
        unreadForAdmins: true,
        closedAt: null,
      });
      batch.set(messageRef, {
        body: body.trim(),
        authorId: currentUser.uid,
        authorName: name,
        authorRole: 'owner',
        attachments,
        createdAt: serverTimestamp(),
      });
      await batch.commit();
      toast.success('Chamado aberto.');
      reset();
      onClose();
      navigate(`/support/${ticketRef.id}`);
    } catch (e) {
      console.error('[open ticket]', e);
      toast.error('Não foi possível abrir o chamado.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Abrir chamado de suporte"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={submit} isLoading={saving}>
            Abrir chamado
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {settings.welcomeMessage ? (
          <Alert variant="info">{settings.welcomeMessage}</Alert>
        ) : null}

        <FormField label="Assunto" required>
          {(id) => (
            <Input
              id={id}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={saving}
              autoFocus
              placeholder="Resuma o problema em uma frase"
            />
          )}
        </FormField>

        <div className={styles.row}>
          <FormField label="Categoria">
            {(id) => (
              <Select
                id={id}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={saving}
                options={categoryOptions}
              />
            )}
          </FormField>
          <FormField label="Prioridade">
            {(id) => (
              <Select
                id={id}
                value={priority}
                onChange={(e) => setPriority(e.target.value as SupportPriority)}
                disabled={saving}
                options={priorityOptions}
              />
            )}
          </FormField>
        </div>

        <FormField label="Descrição" required>
          {(id) => (
            <textarea
              id={id}
              className={styles.textarea}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={saving}
              placeholder="Descreva o passo a passo e o que esperava que acontecesse"
            />
          )}
        </FormField>

        <FormField label="Anexos (opcional)">
          {(id) => (
            <div className={styles.files}>
              <input
                ref={fileRef}
                id={id}
                type="file"
                accept="image/*,application/pdf"
                multiple
                hidden
                disabled={saving}
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              />
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Paperclip size={16} />}
                onClick={() => fileRef.current?.click()}
                disabled={saving}
              >
                Anexar print ou PDF
              </Button>
              {files.length > 0 ? (
                <span className={styles.fileNames}>{files.map((f) => f.name).join(', ')}</span>
              ) : null}
            </div>
          )}
        </FormField>
      </div>
    </Modal>
  );
}
